import React from "react";
import ReactDOM from "react-dom";
import clsx from "clsx";

type Props = {
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'warning' | 'info';
    onConfirm: () => void;
    onCancel: () => void;
};

// Portal for rendering outside SVG
class ConfirmDialogPortal extends React.Component<{ children: React.ReactNode }> {
    private el: HTMLDivElement;

    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.el = document.createElement("div");
        this.el.className = "fixed inset-0 z-[10000]";
    }

    componentDidMount() {
        document.body.appendChild(this.el);
    }

    componentWillUnmount() {
        document.body.removeChild(this.el);
    }

    render() {
        return ReactDOM.createPortal(this.props.children, this.el);
    }
}

export default class ConfirmDialog extends React.Component<Props> {
    private containerRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        // Listen for keyboard events
        document.addEventListener("keydown", this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown);
    }

    handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            e.preventDefault();
            this.props.onCancel();
        } else if (e.key === "Enter") {
            e.preventDefault();
            this.props.onConfirm();
        }
    };

    handleBackdropClick = (e: React.MouseEvent) => {
        // Only cancel if clicking the backdrop, not the dialog content
        if (e.target === e.currentTarget) {
            this.props.onCancel();
        }
    };

    render() {
        const {
            title,
            message,
            confirmLabel = "Confirm",
            cancelLabel = "Cancel",
            variant = 'warning'
        } = this.props;

        const isWarning = variant === 'warning';

        return (
            <ConfirmDialogPortal>
                <div
                    className="fixed inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm animate-dialog-fade-in"
                    onClick={this.handleBackdropClick}
                >
                    <div
                        ref={this.containerRef}
                        className={clsx(
                            "bg-[rgba(28,30,36,0.98)] rounded-xl p-5 font-sans backdrop-blur-[16px] max-w-md min-w-[320px] animate-dialog-slide-in",
                            isWarning
                                ? "shadow-[0_12px_48px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,180,100,0.2)]"
                                : "shadow-[0_12px_48px_rgba(0,0,0,0.6),0_0_0_1px_rgba(100,255,180,0.2)]"
                        )}
                    >
                        <div className="mb-3">
                            <span className={clsx(
                                "text-base font-semibold",
                                isWarning ? "text-warning" : "text-accent"
                            )}>{title}</span>
                        </div>
                        <div className="mb-4">
                            <p className="text-sm leading-relaxed text-white/80 m-0 whitespace-pre-wrap">{message}</p>
                        </div>
                        <div className="flex justify-end gap-2.5">
                            <button
                                className="px-[18px] py-2.5 text-[13px] font-medium bg-white/[0.08] border border-white/15 rounded-md text-white/70 cursor-pointer font-sans transition-all duration-150 hover:bg-white/[0.12]"
                                onClick={this.props.onCancel}
                            >
                                {cancelLabel}
                            </button>
                            <button
                                className={clsx(
                                    "px-[18px] py-2.5 text-[13px] font-medium border rounded-md cursor-pointer font-sans transition-all duration-150",
                                    isWarning
                                        ? "bg-warning/20 border-warning/40 text-warning hover:bg-warning/30"
                                        : "bg-accent/20 border-accent/40 text-accent hover:bg-accent/30"
                                )}
                                onClick={this.props.onConfirm}
                            >
                                {confirmLabel}
                            </button>
                        </div>
                        <div className="mt-3 text-[11px] text-white/35 text-center">
                            Press <kbd className="bg-white/10 px-1.5 py-0.5 rounded text-[10px] font-sans">Enter</kbd> to confirm, <kbd className="bg-white/10 px-1.5 py-0.5 rounded text-[10px] font-sans">Esc</kbd> to cancel
                        </div>
                    </div>
                </div>
            </ConfirmDialogPortal>
        );
    }
}

export { ConfirmDialog };
